import React, {useEffect, useRef, useState} from 'react';
import {ISavedSearchStorefrontDetails} from '../../models/Search';
import {Callout, Checkbox, DirectionalHint, Icon, IconButton, Stack, TooltipHost} from '@fluentui/react';
import NumberField from '../Inputs/NumberField';
import MarkupTypeSelection from '../Storefront/MarkupTypeSelection';
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {
    useLazyGetSavedSearchOnStorefrontCountQuery
} from "../../store/endpoints/SavedSearchEndpoints";
import {
    useGetGlobalStorefrontSettingsQuery,
    useGetStorefrontQuery
} from "../../store/endpoints/StorefrontSettingsEndpoints";
import {IIconProps} from "@fluentui/react/lib-amd/Icon";
import StorefrontDetailsPanel from "../Storefront/StorefrontDetailsPanel";
import {useGetOrganizationEntitlementsQuery} from "../../store/endpoints/OrganizationsEndpoints";
import {CollapsibleColumn} from "./SavedSearchStorefrontSettings";


interface ISavedSearchStorefrontEntryDetailsProps {
    storefrontId?: string;
    storefrontName?: string
    storefrontEntry?: ISavedSearchStorefrontDetails;
    included?: boolean;
    onIncludedChange?: (included: boolean) => void;
    onStorefrontEntryChange: (storefrontEntry: ISavedSearchStorefrontDetails) => void;
    reachedTotalStorefrontLimit?: boolean;
    includeAllStorefronts?: boolean;
    savedSearchId: string;
    hiddenColumns?: CollapsibleColumn[];
}

const DEFAULT_STOREFRONT_ENTRY: ISavedSearchStorefrontDetails = {
    storefrontId: '',
    markup: 0,
    markupType: '',
}

const SavedSearchStorefrontEntryDetails: React.FC<ISavedSearchStorefrontEntryDetailsProps> = (props) => {
    const hidden = props.hiddenColumns ?? [];
    
    const [storefrontEntry, setStorefrontEntry] = useState<ISavedSearchStorefrontDetails | undefined>(props.storefrontEntry);
    const [detailsPanelOpen, setDetailsPanelOpen] = useState<boolean>(false);
    const [moreCalloutOpen, setMoreCalloutOpen] = useState<boolean>(false);
    const moreButtonRef = useRef<HTMLSpanElement>(null);
    const storefront = useGetStorefrontQuery(props.storefrontId ?? '', {skip: !props.storefrontId || props.storefrontId === ''});
    const storefrontGlobalSettings = useGetGlobalStorefrontSettingsQuery();
    useEffect(() => {
        if(props.storefrontEntry?.storefrontId) {
            if (storefrontEntry) setStorefrontEntry({...props.storefrontEntry});
        }
    }, [props.storefrontEntry]);
    
    const markupChange = (markup?:number) => {
        if (!props.storefrontId) return;
        if (!storefrontEntry) props.onStorefrontEntryChange({...DEFAULT_STOREFRONT_ENTRY, storefrontId: props.storefrontId, markup});
        else props.onStorefrontEntryChange({...storefrontEntry, markup});
    }
    const markupTypeChange = (markupType:string) => {
        if (!props.storefrontId) return;
        if (!storefrontEntry) {
            props.onStorefrontEntryChange({...DEFAULT_STOREFRONT_ENTRY, storefrontId: props.storefrontId, markupType: markupType});
        }
        else props.onStorefrontEntryChange({...storefrontEntry, markupType});
    }
    const onIncludedChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, checked?: boolean | undefined) => {
        if (props.onIncludedChange) props.onIncludedChange(checked ?? false);
    }
    
    const onMinimumPriceChange = (minimumPrice?:number) => {
        if (!props.storefrontId) return;
        if (!storefrontEntry) props.onStorefrontEntryChange({...DEFAULT_STOREFRONT_ENTRY, storefrontId: props.storefrontId, minimumPrice});
        else props.onStorefrontEntryChange({...storefrontEntry, minimumPrice});
    }
    const onMinimumCostChange = (minimumCost?:number) => {
        if (!props.storefrontId) return;
        if (!storefrontEntry) props.onStorefrontEntryChange({...DEFAULT_STOREFRONT_ENTRY, storefrontId: props.storefrontId, minimumCost: minimumCost});
        else props.onStorefrontEntryChange({...storefrontEntry, minimumCost});
    }
    const org = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getOrgEntitlements = useGetOrganizationEntitlementsQuery(org ?? '', {skip: !org || org === ''})
    const [perStorefrontCount] = useLazyGetSavedSearchOnStorefrontCountQuery();

    const [reachedLimit, setReachedLimit] = useState<boolean>(false);
    useEffect(() => {
        const limit = getOrgEntitlements.data?.storefrontSavedSearchLimit
        if(props.reachedTotalStorefrontLimit){
            setReachedLimit(true);
        }
        else if(limit && props.storefrontId) {
            perStorefrontCount({storefrontId: props.storefrontId}).unwrap().then((resp => {
                setReachedLimit(resp.count > limit);
            })).catch(() => {
                // Count unknown: leave the toggle as it was before this check existed (not blocked), rather than
                // letting the rejection go unhandled.
                setReachedLimit(false);
            });
        }
        else{
            setReachedLimit(false);
        }
    }, []);
    if(!props.storefrontId || !props.storefrontName || !props.storefrontEntry){
        return <></>
    }
    
    const getIncludedState =() => {
        if (props.includeAllStorefronts) {
            return storefront?.data?.ignoreInheritedProducts ? props.included : true;
        }
        return props.included ?? false;
    }

    const editIcon: IIconProps = { iconName: 'edit' };
    const moreIcon: IIconProps = { iconName: 'More' };

    return (
        <tr>
            <StorefrontDetailsPanel storefrontId={props.storefrontId} globalSettings={storefrontGlobalSettings?.data} isOpen={detailsPanelOpen} onClose={() => setDetailsPanelOpen(false)}/>
            <td>{reachedLimit &&
                <TooltipHost
                    content={'Storefront has reached a maximum number of products and/or saved searches.'}><Icon
                    iconName={'Warning'} color={'red'}/></TooltipHost>
            }
                <IconButton iconProps={editIcon} onClick={() => setDetailsPanelOpen(true)} />
            </td>
            <td style={{minWidth: '200px'}}>{props.storefrontName}</td>
            <td>
                <Checkbox
                    disabled={props.includeAllStorefronts}
                    checked={getIncludedState()}
                    onChange={onIncludedChange}
                />
            </td>
            {!hidden.includes('markup') && (
                <td>
                    <NumberField value={storefrontEntry?.markup} onChange={markupChange} disabled={reachedLimit} decimal={true} />
                </td>
            )}
            {!hidden.includes('markupType') && (
                <td style={{maxWidth: '25%'}}>
                    <MarkupTypeSelection storefrontId={storefrontEntry?.storefrontId} value={storefrontEntry?.markupType} onChange={markupTypeChange} disabled={reachedLimit} />
                </td>
            )}
            {!hidden.includes('minimumPrice') && (
                <td>
                    <NumberField value={storefrontEntry?.minimumPrice} onChange={onMinimumPriceChange} prefix='$' disabled={reachedLimit} decimal={true} />
                </td>
            )}
            {!hidden.includes('minimumCost') && (
                <td>
                    <NumberField value={storefrontEntry?.minimumCost} onChange={onMinimumCostChange} prefix='$' disabled={reachedLimit} />
                </td>
            )}
            {hidden.length > 0 && (
                <td>
                    <span ref={moreButtonRef} style={{ display: 'inline-block' }}>
                        <IconButton
                            iconProps={moreIcon}
                            title="More options"
                            onClick={() => setMoreCalloutOpen(v => !v)}
                        />
                    </span>
                    {moreCalloutOpen && (
                        <Callout
                            target={moreButtonRef}
                            onDismiss={() => setMoreCalloutOpen(false)}
                            directionalHint={DirectionalHint.bottomRightEdge}
                            gapSpace={4}
                            styles={{ root: { padding: '12px 16px', minWidth: 200 } }}
                        >
                            <Stack tokens={{ childrenGap: 8 }}>
                                {hidden.includes('markup') && (
                                    <NumberField label="Markup" value={storefrontEntry?.markup} onChange={markupChange} disabled={reachedLimit} decimal={true} />
                                )}
                                {hidden.includes('markupType') && (
                                    <MarkupTypeSelection label="Markup Type" storefrontId={storefrontEntry?.storefrontId} value={storefrontEntry?.markupType} onChange={markupTypeChange} disabled={reachedLimit} />
                                )}
                                {hidden.includes('minimumPrice') && (
                                    <NumberField label="Minimum Price" value={storefrontEntry?.minimumPrice} onChange={onMinimumPriceChange} prefix='$' disabled={reachedLimit} decimal={true} />
                                )}
                                {hidden.includes('minimumCost') && (
                                    <NumberField label="Minimum Cost" value={storefrontEntry?.minimumCost} onChange={onMinimumCostChange} prefix='$' disabled={reachedLimit} />
                                )}
                            </Stack>
                        </Callout>
                    )}
                </td>
            )}
        </tr>
    );
}

export default SavedSearchStorefrontEntryDetails;